import { Tray } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";

import {
  type ProjectFeedEvent,
  type ProjectImportPayload,
} from "../../../services/askPunttProjectFeed.types";

/**
 * Integration / inbox import card (mockup: "Spring '26 — Creative Brief
 * imported", from Sandra Ruiz · Brand Lead, RECEIVED). Surfaced when Puntt
 * auto-files a connector asset into the project's context.
 */
export function FeedImportCard({ event }: { event: ProjectFeedEvent }) {
  const { t } = useTranslation("ui");
  const payload = (event.payload ?? {}) as ProjectImportPayload;
  const title = payload.title ?? event.body ?? t("feedImportCard.defaultTitle");
  const sourceName = payload.sourceName;
  const sourceRole = payload.sourceRole;
  const status = payload.status ?? t("feedImportCard.defaultStatus");
  const titleId = `feed-import-title-${event._id}`;

  return (
    <section
      className="rounded-xl border border-puntt-neutral-gray-5 bg-base-white p-3.5"
      data-testid="project-feed-import-card"
      aria-labelledby={titleId}
    >
      <div className="flex items-start gap-3">
        <span
          aria-hidden="true"
          className="flex size-9 shrink-0 items-center justify-center rounded-md bg-puntt-blue-2 text-puntt-accent-9"
        >
          <Tray size={18} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3
              id={titleId}
              className="min-w-0 flex-1 truncate text-[13px] font-semibold text-puntt-neutral-gray-12"
            >
              {title}
            </h3>
            <span
              className="bg-puntt-green-3 text-puntt-green-11 shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide"
              data-testid="project-feed-import-status"
            >
              {status}
            </span>
          </div>
          {(sourceName || sourceRole) && (
            <p
              className="mt-1 text-[12px] text-puntt-neutral-gray-11"
              data-testid="project-feed-import-source"
            >
              {sourceName
                ? t("feedImportCard.fromSource", { sourceName })
                : t("feedImportCard.fromIntegration")}
              {sourceRole ? `, ${sourceRole}` : ""}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
